import {
  collection,
  query,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '../firebase/firebase';
import { StoreProduct } from '../models/StoreProduct';

class StoreProductsService {
  constructor() {
    this.collectionName = 'storeProducts';
  }

  /**
   * Get all active products, sorted by name
   */
  async getAllProducts() {
    const collectionRef = collection(db, this.collectionName);
    const q = query(collectionRef, orderBy('name'));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => StoreProduct.fromFirebase(doc))
      .filter(p => p.active);
  }

  /**
   * Search products by name (case-insensitive client-side filtering)
   * @param {string} searchTerm - The search query
   * @param {number} pageSize - Number of results per page
   * @returns {Array} Filtered products
   */
  async searchProducts(searchTerm, pageSize = 10) {
    // Firestore doesn't support case-insensitive search natively
    // We'll fetch all and filter client-side for now
    // For larger catalogs, consider Algolia or Firebase Extensions
    const products = await this.getAllProducts();

    if (!searchTerm || searchTerm.trim() === '') {
      return products.slice(0, pageSize);
    }

    const term = searchTerm.toLowerCase().trim();
    return products
      .filter(p => p.name.toLowerCase().includes(term))
      .slice(0, pageSize);
  }

  /**
   * Subscribe to all products with real-time updates
   * @param {Function} callback - Called with array of products
   * @returns {Function} Unsubscribe function
   */
  subscribeProducts(callback) {
    const collectionRef = collection(db, this.collectionName);
    const q = query(collectionRef, orderBy('name'));

    return onSnapshot(q, (snapshot) => {
      const products = snapshot.docs
        .map(doc => StoreProduct.fromFirebase(doc))
        .filter(p => p.active);
      callback(products);
    }, (error) => {
      console.error('Error listening to store products:', error);
      callback([]);
    });
  }

  /**
   * Add a new product
   * @param {Object} productData - Product data
   * @returns {StoreProduct} Created product
   */
  async addProduct({ name, orderUnit, pricingUnit, pricePerPricingUnit, estimatedConversion, unitMeasure }) {
    const collectionRef = collection(db, this.collectionName);

    const product = new StoreProduct({
      id: null,
      name,
      orderUnit: orderUnit || 'each',
      pricingUnit: pricingUnit || 'each',
      pricePerPricingUnit: pricePerPricingUnit || 0,
      estimatedConversion: orderUnit !== pricingUnit ? estimatedConversion : null,
      unitMeasure: unitMeasure || null,
      active: true,
      createdAt: serverTimestamp(),
    });

    const docRef = await addDoc(collectionRef, product.toJson());
    product.id = docRef.id;
    return product;
  }

  /**
   * Update an existing product
   * @param {string} id - Product ID
   * @param {Object} data - Fields to update
   */
  async updateProduct(id, { name, orderUnit, pricingUnit, pricePerPricingUnit, estimatedConversion, unitMeasure }) {
    const docRef = doc(db, this.collectionName, id);
    await updateDoc(docRef, {
      name,
      orderUnit,
      pricingUnit,
      pricePerPricingUnit,
      estimatedConversion: orderUnit !== pricingUnit ? estimatedConversion : null,
      unitMeasure: unitMeasure || null,
      lastUpdatedAt: serverTimestamp(),
    });
  }

  /**
   * Deactivate a product (soft delete)
   * @param {string} id - Product ID
   */
  async deactivateProduct(id) {
    const docRef = doc(db, this.collectionName, id);
    await updateDoc(docRef, { active: false });
  }
}

const storeProductsService = new StoreProductsService();
export default storeProductsService;
